import { useId } from 'react';
import type { ButtonHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react';

import { cx } from './cx';

// Primitive barrel — one import surface for the whole design system.
export { Badge } from './Badge';
export type { BadgeTone } from './Badge';
export { BarList } from './BarList';
export type { BarListItem } from './BarList';
export { Breadcrumb } from './Breadcrumb';
export { PageHeader } from './PageHeader';
export { DataTable } from './DataTable';
export type { ColumnAlign, DataTableColumn } from './DataTable';
export { EmptyState } from './EmptyState';
export { FileDropzone } from './FileDropzone';
export { DateInput, FormField, Select, TextInput } from './FormField';
export { MultiSelectChips } from './MultiSelectChips';
export { SelectWithCaption } from './SelectWithCaption';
export { StatCard } from './StatCard';
export { Tabs } from './Tabs';
export type { TabItem } from './Tabs';
export { ToastProvider, useToast } from './Toast';
export type { ToastTone } from './Toast';
export { WizardProgress } from './WizardProgress';

type ButtonVariant = 'primary' | 'secondary' | 'ghost';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
}

export function Button({ children, className, variant = 'primary', ...props }: ButtonProps) {
  return (
    <button className={cx('rfp-button', `rfp-button--${variant}`, className)} {...props}>
      {children}
    </button>
  );
}

interface EyebrowProps {
  children: ReactNode;
  className?: string;
  trailing?: ReactNode;
}

export function Eyebrow({ children, className, trailing }: EyebrowProps) {
  return (
    <div className={cx('rfp-eyebrow', className)}>
      <span aria-hidden="true" className="rfp-eyebrow__rule" />
      <span>{children}</span>
      {trailing && <span>{trailing}</span>}
    </div>
  );
}

interface WorkflowCardProps {
  action?: ReactNode;
  children: ReactNode;
  step: string;
  title: string;
}

export function WorkflowCard({ action, children, step, title }: WorkflowCardProps) {
  return (
    <article className="workflow-cell">
      <span className="workflow-icon">{step}</span>
      <h2>{title}</h2>
      <p>{children}</p>
      {action}
    </article>
  );
}

interface ModalProps {
  actions?: ReactNode;
  children: ReactNode;
  title: string;
}

export function Modal({ actions, children, title }: ModalProps) {
  const titleId = useId();

  return (
    <div className="modal-backdrop" role="presentation">
      <section aria-labelledby={titleId} aria-modal="true" className="file-modal" role="dialog">
        <h2 id={titleId}>{title}</h2>
        {children}
        {actions && <div className="modal-actions">{actions}</div>}
      </section>
    </div>
  );
}

interface TextAreaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
}

export function TextAreaField({ className, id, label, ...props }: TextAreaFieldProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  return (
    <div className={cx('field-stack', className)}>
      <label className="ai-prompt-label" htmlFor={inputId}>
        {label}
      </label>
      <textarea id={inputId} {...props} />
    </div>
  );
}

type StatusTone = 'neutral' | 'success' | 'warning' | 'danger';

interface StatusPillProps {
  children: ReactNode;
  tone?: StatusTone;
}

export function StatusPill({ children, tone = 'neutral' }: StatusPillProps) {
  return <span className={cx('status-pill', `status-pill--${tone}`)}>{children}</span>;
}
